// Small split-button for exporting / copying a result grid. Reused by the
// table-data toolbar and the query-result tabs bar.
//
// Props:
//   spec()      fn → base export spec: { kind, connectionId, database, table?, filters?, sorts?, sql? }
//               (format is filled in per action)
//   columns()   fn → [{name,...}]  — for "copy rows on screen"
//   rows()      fn → any[][]        — for "copy rows on screen"
//   displayOrder()  fn → string[]   — optional: column names in display order
//                                      (honours user reordering / hiding for the image)
//   imageMeta() fn → { label, sql, database, createdAt, server } — optional;
//                    powers the "📷 Copy as image" header (title, db, timestamp).
//                    When omitted, the image item is hidden.
//   sqlDump     bool — show the SQL-dump item (table only)
//   disabled    bool — no data to export
import { createSignal, Show } from 'solid-js';
import { useDismissable } from '../lib/dismissable.js';
import { downloadExport, copyFullToClipboard, copyRowsToClipboard } from '../lib/export.js';
import { copyResultImage } from '../lib/resultimage.js';
import { formatBytes } from '../lib/format.js';
import { toast } from '../lib/toast.js';

export default function ExportMenu(props) {
  const [open, setOpen] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  let btnRef, menuRef;
  useDismissable(() => [btnRef, menuRef], () => setOpen(false), { event: 'click' });

  const run = async (fn, ok) => {
    setOpen(false);
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok(r));
    } catch (e) {
      toast.error('Export failed: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const dl = (format) => run(
    () => downloadExport({ ...props.spec(), format }),
    () => `${format.toUpperCase()} download started`,
  );
  const copyScreen = () => run(
    () => copyRowsToClipboard(props.columns(), props.rows()),
    (n) => `Copied ${n} row${n === 1 ? '' : 's'} to clipboard`,
  );
  const copyFull = () => run(
    () => copyFullToClipboard(props.spec()),
    (chars) => `Copied full result (${formatBytes(chars)}) to clipboard`,
  );
  // PNG snapshot — auto-fit columns, header above with title/db/timestamp.
  // Capped at 500 rows (rest noted in a footer line on the image itself).
  const copyImage = () => run(
    () => {
      const meta = props.imageMeta?.() || {};
      return copyResultImage({
        label:     meta.label,
        sql:       meta.sql,
        database:  meta.database,
        createdAt: meta.createdAt,
        server:    meta.server,
        columns:     props.columns?.() || [],
        rows:        props.rows?.() || [],
        displayOrder: props.displayOrder?.() || null,
      });
    },
    (bytes) => `Copied image to clipboard (${formatBytes(bytes)})`,
  );

  return (
    <div class="export-menu">
      <button ref={btnRef} class="btn btn-sm btn-ghost"
              disabled={props.disabled || busy()}
              onClick={() => setOpen(!open())}
              title="Export or copy these results">
        {busy() ? '⏳ …' : '⬇ Export ▾'}
      </button>
      <Show when={open()}>
        <div ref={menuRef} class="export-menu-pop">
          <button class="export-menu-item" onClick={() => dl('csv')}>CSV file (.csv)</button>
          <button class="export-menu-item" onClick={() => dl('xlsx')}>Excel file (.xlsx)</button>
          <button class="export-menu-item" onClick={() => dl('json')}>JSON file (.json)</button>
          <button class="export-menu-item" onClick={() => dl('jsonl')}>JSON Lines (.jsonl)</button>
          <button class="export-menu-item" onClick={() => dl('md')}>Markdown table (.md)</button>
          <Show when={props.sqlDump}>
            <button class="export-menu-item" onClick={() => dl('sql')}>SQL INSERTs (.sql)</button>
          </Show>
          <div class="export-menu-sep" />
          <button class="export-menu-item" onClick={copyScreen}>Copy rows on screen</button>
          <button class="export-menu-item" onClick={copyFull}>Copy full result</button>
          <Show when={props.imageMeta}>
            <button class="export-menu-item" onClick={copyImage}
                    title="Render the table as a PNG image and copy to clipboard. Columns auto-fit to content. First 500 rows; the rest are noted in a footer line on the image.">
              📷 Copy as image (PNG)
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}
